import { useState, useEffect, useRef } from 'react';

const pillars = [
  {
    id: '01',
    title: 'Horizonte Arraigado',
    subtitle: 'Grounding',
    desc: 'Ancla tu campo energético a la frecuencia de la Tierra. Libera la Tensión Nodal acumulada y activa tu sistema de raíces para una presencia plena y magnetismo natural.',
    tag: 'Tierra & Presencia',
    img: 'https://public.readdy.ai/ai/img_res/f5b1a460b0968056dcf02f4e9afd80c8.jpg',
    accent: '#C97B63',
  },
  {
    id: '02',
    title: 'Bio-Santuario',
    subtitle: 'Habitabilidad',
    desc: 'Transforma tu entorno en un campo de coherencia. La Bio-habitabilidad es la tecnología de los espacios vivos: cada objeto, luz y frecuencia en tu hogar impacta tu campo sistémico.',
    tag: 'Entorno & Espacio',
    img: 'https://readdy.ai/api/search-image?query=sacred%20biophilic%20interior%20space%20with%20geometric%20botanical%20patterns%20sage%20green%20plants%20forming%20perfect%20geometric%20arrangements%20architectural%20clean%20lines%20bio-sanctuary%20sacred%20geometry%20warm%20terracotta%20lighting%20minimal%20aesthetic%20dark%20atmospheric&width=600&height=800&seq=pillar2-nube-001&orientation=portrait',
    accent: '#8B9D83',
  },
  {
    id: '03',
    title: 'Alquimia Mental',
    subtitle: 'Reprogramación',
    desc: 'Accede a los patrones subconscientes que generan tus loops sistémicos. Mediante metodologías de neuroplasticidad y geometría mental, disuelve creencias limitantes en tiempo real.',
    tag: 'Mente & Creencias',
    img: 'https://readdy.ai/api/search-image?query=abstract%20neural%20network%20sacred%20geometry%20brain%20mind%20map%20visualization%20silver%20and%20charcoal%20tones%20synaptic%20connections%20forming%20geometric%20patterns%20consciousness%20reprogramming%20visualization%20dark%20charcoal%20background%20glowing%20nodes%20minimal&width=600&height=800&seq=pillar3-nube-001&orientation=portrait',
    accent: '#C97B63',
  },
  {
    id: '04',
    title: 'Astrolabio Interior',
    subtitle: 'Elementos',
    desc: 'Los cuatro elementos no son metáforas: son estados de conciencia. Aprende a navegar entre Fuego, Agua, Tierra y Aire como un astrolabio viviente que orienta tu expansión.',
    tag: 'Elementos & Cosmos',
    img: 'https://readdy.ai/api/search-image?query=ancient%20astronomical%20astrolabe%20sacred%20geometry%20cosmic%20star%20map%20gold%20and%20sage%20green%20tones%20four%20elements%20symbols%20fire%20water%20earth%20air%20in%20geometric%20mandala%20celestial%20precision%20dark%20cosmic%20background%20minimal%20mystical&width=600&height=800&seq=pillar4-nube-001&orientation=portrait',
    accent: '#8B9D83',
  },
  {
    id: '05',
    title: 'Maestría Multidimensional',
    subtitle: 'Soberanía',
    desc: 'La soberanía personal no es una actitud: es una configuración sistémica. Domina las capas vibracionales de tu experiencia y opera desde el centro de tu campo de poder.',
    tag: 'Soberanía & Expansión',
    img: 'https://readdy.ai/api/search-image?query=infinite%20geometric%20fractal%20patterns%20radiating%20from%20center%20point%20sovereignty%20crown%20sacred%20geometry%20deep%20space%20cosmic%20backdrop%20infinite%20dimension%20visualization%20terracotta%20and%20charcoal%20minimal%20dark%20background%20mystical%20powerful&width=600&height=800&seq=pillar5-nube-001&orientation=portrait',
    accent: '#C97B63',
  },
  {
    id: '06',
    title: 'Blueprint Sistémico',
    subtitle: 'Constelaciones',
    desc: 'Las Constelaciones Familiares revelan los órdenes sistémicos que operan en tu campo. La Reparación Sistémica libera patrones transgeneracionales y activa tu flujo de Ayni.',
    tag: 'Sistema & Familia',
    img: 'https://readdy.ai/api/search-image?query=family%20constellation%20map%20rendered%20as%20geometric%20star%20chart%20interconnected%20nodes%20and%20luminous%20lines%20forming%20family%20tree%20structure%20blueprint%20style%20sage%20green%20and%20off-white%20on%20dark%20background%20systemic%20visualization%20minimal%20spiritual&width=600&height=800&seq=pillar6-nube-001&orientation=portrait',
    accent: '#8B9D83',
  },
];

export default function CartografiaSection() {
  const [active, setActive] = useState(0);
  const [visible, setVisible] = useState(false);
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section id="cartografia" ref={sectionRef} className="bg-[#F8F6F3] py-32 px-6">
      <div className="max-w-screen-xl mx-auto">
        {/* Header */}
        <div
          className={`text-center mb-20 transition-all duration-1000 ${
            visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          <p className="text-[#8B9D83] text-xs uppercase tracking-[4px] mb-4">— La Arquitectura —</p>
          <h2
            className="text-[#2A2D34] font-bold mb-4 leading-tight"
            style={{ fontSize: 'clamp(2.2rem, 4.5vw, 3.5rem)' }}
          >
            Los 6 Pilares de tu Expansión
          </h2>
          <p className="text-[#2A2D34]/60 text-base max-w-lg mx-auto">
            Cada pilar es un nodo en tu arquitectura sistémica. Juntos forman la Nube Geodésica completa.
          </p>
        </div>

        {/* Cards grid */}
        <div
          className={`grid grid-cols-2 lg:grid-cols-3 gap-5 transition-all duration-1000 delay-200 ${
            visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'
          }`}
        >
          {pillars.map((p, i) => (
            <div
              key={p.id}
              className={`relative rounded-2xl overflow-hidden cursor-pointer transition-all duration-500 ${
                active === i ? 'ring-2' : 'ring-0 hover:ring-1'
              }`}
              style={{
                height: active === i ? '440px' : '340px',
                ringColor: p.accent,
              }}
              onClick={() => setActive(i)}
            >
              {/* Background image */}
              <img
                src={p.img}
                alt={p.title}
                className="absolute inset-0 w-full h-full object-cover object-top transition-transform duration-700 hover:scale-105"
              />
              {/* Gradient overlay */}
              <div
                className={`absolute inset-0 transition-opacity duration-500 ${
                  active === i ? 'opacity-90' : 'opacity-80'
                }`}
                style={{
                  background: `linear-gradient(to top, ${p.accent === '#C97B63' ? '#1A1C20' : '#1A2A20'} 0%, transparent 60%)`,
                }}
              />

              {/* Top accent line */}
              {active === i && (
                <div
                  className="absolute top-0 left-0 h-1 w-12 rounded-br-full transition-all duration-300"
                  style={{ backgroundColor: p.accent }}
                />
              )}

              {/* Content */}
              <div className="absolute bottom-0 left-0 right-0 p-6">
                <div className="flex items-center gap-2 mb-2">
                  <span
                    className="text-xs font-bold tracking-[1px]"
                    style={{ color: p.accent }}
                  >
                    {p.id}
                  </span>
                  <span className="text-[#F8F6F3]/40 text-xs">— {p.tag}</span>
                </div>
                <h3 className="text-[#F8F6F3] font-bold text-xl leading-tight mb-1">{p.title}</h3>
                <p className="text-[#F8F6F3]/60 text-xs uppercase tracking-[2px] mb-3">{p.subtitle}</p>

                {active === i && (
                  <p className="text-[#F8F6F3]/80 text-sm leading-relaxed mt-3 border-t border-[#F8F6F3]/10 pt-3">
                    {p.desc}
                  </p>
                )}

                {active !== i && (
                  <div className="flex items-center gap-1 text-xs" style={{ color: p.accent }}>
                    <span>Ver pilar</span>
                    <i className="ri-arrow-right-line text-xs" />
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>

        {/* Bottom CTA */}
        <div className="text-center mt-16">
          <p className="text-[#2A2D34]/50 text-sm mb-6">
            Ocupa tu nodo. Expande tu arquitectura interior.
          </p>
          <button
            onClick={() => document.querySelector('#planes')?.scrollIntoView({ behavior: 'smooth' })}
            className="bg-[#2A2D34] text-[#F8F6F3] font-medium text-sm px-8 py-3.5 rounded-full hover:bg-[#C97B63] transition-all duration-300 whitespace-nowrap cursor-pointer"
          >
            Acceder a los 6 Pilares Completos
          </button>
        </div>
      </div>
    </section>
  );
}
